import { useState } from "react";
import type { HotelTripProduct } from "@b2bportal/core-hotels";
import { useModuleBEM } from "@b2bportal/core-themes";
import { TrackedLoadedPageName } from "@b2bportal/core-tracking";
import { Product } from "@b2bportal/purchase-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { TripsList } from "@tenant/trips";
import { Page } from "@tenant/ui";
import styles from "./MyTripsPage.module.scss";
import { FolderTabs } from "@/components";
import { useRefreshPoints } from "@/components/AuthModule/hooks";
import type { CarTripProduct } from "@/pages/Trips/hooks/useCarsTripProduct";

export type ListProducts = {
  [Product.Hotel]: HotelTripProduct;
  [Product.Ground]?: CarTripProduct;
};

type MyTripsPageProps = {
  products: ListProducts;
  pastProducts: ListProducts;
};

type TripsTabProps = {
  isVisible: boolean;
  pageContext: TripsPageContext;
  products: ListProducts;
};

const TripsTab = ({ isVisible, pageContext, products }: TripsTabProps) => {
  const [_block, cn] = useModuleBEM(styles, "MyTripsPage");
  return isVisible ? (
    <div className={cn("trips-tab", { visible: isVisible })}>
      <TripsList products={products} pageContext={pageContext} />
    </div>
  ) : null;
};

export enum TripsPageContext {
  Upcoming = "upcoming",
  Completed = "completed",
  Guest = "guest",
}
export const MyTripsPage = ({ products, pastProducts }: MyTripsPageProps) => {
  const [block, cn] = useModuleBEM(styles, "MyTripsPage");
  const [activeTab, setActiveTab] = useState(TripsPageContext.Upcoming);
  const { t } = useI18nContext();

  useRefreshPoints();

  const tabs = [
    {
      value: TripsPageContext.Upcoming,
      label: t("trips.myTripsPage.tabs.upcoming"),
      products,
    },
    {
      value: TripsPageContext.Completed,
      label: t("trips.myTripsPage.tabs.completed"),
      products: pastProducts,
    },
  ];

  const hasItems = (products: ListProducts) => {
    const hotelCount = products[Product.Hotel]?.reservations.data?.length ?? 0;
    const carCount = products[Product.Ground]?.reservations.data?.length ?? 0;
    return hotelCount > 0 || carCount > 0;
  };

  // We don't show tabs unless you already have at least one booking. This is to avoid confusion around empty states.
  const showTabs = hasItems(products) || hasItems(pastProducts);

  return (
    <Page trackingName={TrackedLoadedPageName.TripsList}>
      <div className={block}>
        <div className={cn("container")}>
          <div className={cn("content")}>
            <div className={cn("title-container")}>
              <h1 className={cn("title")}>{t("trips.myTripsPage.title")}</h1>
              <p className={cn("subtitle")}>
                {t("trips.myTripsPage.subtitle")}
              </p>
            </div>
            {showTabs && (
              <div className={cn("tabs-container")}>
                <div className={cn("tabs-content")}>
                  <FolderTabs
                    active={activeTab}
                    setActive={setActiveTab}
                    options={tabs}
                  />
                </div>
              </div>
            )}
            {tabs.map(({ value, products }) => (
              <TripsTab
                key={value}
                pageContext={value}
                isVisible={activeTab === value}
                products={products}
              />
            ))}
          </div>
        </div>
      </div>
    </Page>
  );
};
